import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn, formatTurkishPhoneForDatabase } from '@/lib/utils';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/hooks/use-auth';
import type { Tables } from '@/integrations/supabase/types';

type CustomerColor = Tables<'customers'>['color'];
type ManualTag = Tables<'customers'>['tags_manual'];

interface EditCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    birth_date?: string | null;
    anniversary_date?: string | null;
    color?: CustomerColor | null;
    tags_manual?: ManualTag | null;
  };
  onCustomerUpdated: () => void;
}

const colorOptions: Array<{ value: CustomerColor, label: string, bgClass: string, info: string }> = [
  { value: 'mavi', label: 'Mavi', bgClass: 'bg-blue-500/20', info: 'Standart Müşteri' },
  { value: 'kirmizi', label: 'Kırmızı', bgClass: 'bg-red-500/20', info: 'Dikkat!' },
  { value: 'yesil', label: 'Yeşil', bgClass: 'bg-green-500/20', info: 'Opsiyonel' },
  { value: 'sari', label: 'Sarı', bgClass: 'bg-yellow-500/20', info: 'Opsiyonel' },
  { value: 'siyah', label: 'Siyah', bgClass: 'bg-gray-500/20', info: 'Özel Müşteri Mobil Bildirim Gider' },
  { value: 'beyaz', label: 'Beyaz', bgClass: 'bg-slate-100/80', info: 'Çalışan' },
  { value: 'gold', label: 'Altın', bgClass: 'bg-yellow-600/20', info: 'Harcamaya göre VIP sistem tarafından otomatik atanır' },
];

type ManualTagEnum = 'vip' | 'sadik' | 'sik_soran' | 'fiyat_duyarli' | 'pazarlikci' | 'memnuniyetsiz_gecmis' | 'servis_bekliyor' | 'montaj_bekliyor' | 'kurumsal' | 'taksit_agirlikli' | 'aksesuar_odakli' | 'yakini_es_dost' | 'geri_aransin' | 'kampanya_ilgili';

const manualTagOptions: Array<{ value: ManualTagEnum, label: string }> = [
  { value: 'vip', label: 'VIP' },
  { value: 'sadik', label: 'Sadık Müşteri' },
  { value: 'sik_soran', label: 'Sık Soran' },
  { value: 'fiyat_duyarli', label: 'Fiyat Duyarlı' },
  { value: 'pazarlikci', label: 'Pazarlıkçı' },
  { value: 'memnuniyetsiz_gecmis', label: 'Memnuniyetsiz Geçmiş' },
  { value: 'servis_bekliyor', label: 'Servis Bekliyor' },
  { value: 'montaj_bekliyor', label: 'Montaj Bekliyor' },
  { value: 'kurumsal', label: 'Kurumsal' },
  { value: 'taksit_agirlikli', label: 'Taksit Ağırlıklı' },
  { value: 'aksesuar_odakli', label: 'Aksesuar Odaklı' },
  { value: 'yakini_es_dost', label: 'Yakını Eş/Dost' },
  { value: 'geri_aransin', label: 'Geri Aransın' },
  { value: 'kampanya_ilgili', label: 'Kampanya İlgili' },
];

export const EditCustomerModal = ({ 
  open, 
  onOpenChange, 
  customer,
  onCustomerUpdated
}: EditCustomerModalProps) => {
  const { toast } = useToast();
  const { companyId, user } = useAuth();
  const { currentLocation } = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState<Date | undefined>();
  const [anniversaryDate, setAnniversaryDate] = useState<Date | undefined>();
  const [color, setColor] = useState<CustomerColor>('mavi');
  const [selectedManualTags, setSelectedManualTags] = useState<ManualTagEnum[]>([]);

  // Initialize form with customer data
  useEffect(() => {
    if (open && customer) {
      setFirstName(customer.first_name || '');
      setLastName(customer.last_name || '');
      setEmail(customer.email || '');
      setPhone(customer.phone || '');
      setBirthDate(customer.birth_date ? new Date(customer.birth_date) : undefined);
      setAnniversaryDate(customer.anniversary_date ? new Date(customer.anniversary_date) : undefined);
      setColor(customer.color || 'mavi');
      setSelectedManualTags((customer.tags_manual as ManualTagEnum[]) || []);
    }
  }, [open, customer]);

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      const updateData = {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim() || null,
        phone: formatTurkishPhoneForDatabase(phone.trim()),
        birth_date: birthDate ? format(birthDate, 'yyyy-MM-dd') : null,
        anniversary_date: anniversaryDate ? format(anniversaryDate, 'yyyy-MM-dd') : null,
        color: color,
        tags_manual: selectedManualTags.length > 0 ? selectedManualTags : [],
      };

      const { error } = await supabase
        .from('customers')
        .update(updateData)
        .eq('id', customer.id);

      if (error) throw error;

      // Log activity
      if (companyId) {
        await logActivity({
          action: 'update',
          entity: 'customer',
          entityId: customer.id,
          detail: {
            name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            changes: updateData,
          },
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }

      toast({
        title: "Müşteri güncellendi",
        description: "Müşteri bilgileri başarıyla güncellendi.",
      });

      onCustomerUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating customer:', error);
      toast({
        title: "Hata",
        description: "Müşteri güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Müşteri Bilgilerini Düzenle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ad</Label>
              <Input
                placeholder="Ad"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Soyad</Label>
              <Input
                placeholder="Soyad"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-2">
            <Label>E-posta</Label>
            <Input
              type="email"
              placeholder="ornek@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input
              type="tel"
              placeholder="0555 123 45 67"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Doğum Tarihi</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !birthDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {birthDate ? format(birthDate, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={birthDate}
                    onSelect={setBirthDate}
                    disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                    initialFocus
                    locale={tr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Yıldönümü</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !anniversaryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {anniversaryDate ? format(anniversaryDate, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={anniversaryDate}
                    onSelect={setAnniversaryDate}
                    disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                    initialFocus
                    locale={tr}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Color Selection */}
          <div className="space-y-2">
            <Label>Renk Etiketi</Label>
            <Select value={color} onValueChange={(value: CustomerColor) => setColor(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {colorOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${option.bgClass}`} />
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">- {option.info}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Manual Tags Selection */}
          <div className="space-y-2">
            <Label>Manuel Etiketler</Label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
              {manualTagOptions.map((tag) => (
                <div key={tag.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={tag.value}
                    checked={selectedManualTags.includes(tag.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedManualTags([...selectedManualTags, tag.value]);
                      } else {
                        setSelectedManualTags(selectedManualTags.filter(t => t !== tag.value));
                      }
                    }}
                  />
                  <Label
                    htmlFor={tag.value}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {tag.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedManualTags.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedManualTags.length} etiket seçildi
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                'Kaydet'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
